import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, gql, useLazyQuery } from "@apollo/client";
import { Box, Chip, CircularProgress, Typography, Paper, Divider, useTheme } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { useLocation } from "react-router-dom";
import { GET_USER_ANALYTICS, GET_QUESTION_DETAILS, GET_USER_BY_ID, GET_ORG_BY_ID, GET_TRAINING_MODULE__BY_ID, GET_USER_ANALYTICS_BY_USERID_TRAININGID } from "../../Graphql/Queries";
import CancelIcon from '@mui/icons-material/Cancel';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Form, Tag, Tooltip } from "antd";
import FormItemSelect from "../FormItems/FormItemSelect";
import { CREATE_GRADE } from "../../Graphql/Mutations";
import { Error } from "../../Types/MessageType";
import { FormInstance } from "antd/lib/form";
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Image } from "antd";
import { IQTraining} from "../../Enums/IQTraining";
import { TrainingStatus} from "../../Enums/TrainingStatus";
import SkipNextIcon from '@mui/icons-material/SkipNext';

type Attempt = {
  timestamp: string;
  timeTaken: number;
  passed: TrainingStatus;
};

type Scenario = {
  scenarioId: string;
  scenarioName: string;
  attempts: Attempt[];
};

type UserData = {
  userId?: string;
};

type createAnalyticsProp = {
  alertMessage: (val: Error) => void;
};

const CreateAnalytics = ({ alertMessage }: createAnalyticsProp) => {
  const resetAlertTimer = 3000;
  const location = useLocation();
  const [selectedScenario, setSelectedScenario] = useState<string>("");
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [analyticData, setAnalyticData] = useState<any>([]);
  const [steps, setSteps] = useState<any>([]);
  const [selectedGradeId, setSelectedGradeId] = useState<string>();
  const [selectedStep, setSelectedStep] = useState<number | null>(null);
  const [stepData, setStepData] = useState<any>(null);
  const [noData, setNoData] = useState<boolean>(false); // Add this new state

  const [options, setOptions] = useState<any[]>([]);
  // const [grades, setGrades] = useState();
  const [form] = Form.useForm();
  const theme = useTheme();
  const [dropdownClicked, setDropdownClicked] = useState<boolean>(false);
  const [moduleName, setModuleName] = useState<string>('');
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false); // Add this new state
  const [getUserAnalyticsByUserIdTrainingId, { data: stepsData, loading: stepLoading }] = useLazyQuery(GET_USER_ANALYTICS_BY_USERID_TRAININGID);
  const [trainingId, setTrainingId] = useState<string>('');
  const [selectedTrainingId, setSelectedTrainingId] = useState<string | null>(null);

  const formatCompletedAt = (completedAt: string) => {
    const date = new Date(completedAt);
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
      timeZoneName: 'short'
    };
    return date.toLocaleString('en-IN', options);
  };

  const handleStepClick = (stepNo: number) => {
    if (selectedStep === stepNo) {
      setSelectedStep(null);
      setStepData(null);
    } else {
      setSelectedStep(stepNo);
    }
  };

  useEffect(() => {
    if (moduleName && options.length > 0) {
      // Find the training whose name matches moduleName
      const matchedTraining = options.find(training => training.label === moduleName);
      if (matchedTraining) {
        setSelectedTrainingId(matchedTraining.value);
      } else {
        console.warn("No matching training found for module:", moduleName);
      }
    }
  }, [moduleName, options]);

  useEffect(() => {
    if (selectedStep !== null && selectedTrainingId) {
      getUserAnalyticsByUserIdTrainingId({
        variables: {
          userId: (location.state as UserData)?.userId || '',
          trainingId: selectedTrainingId,
          stepNo: selectedStep,
        },
      })
        .then((response) => {
          setStepData(response.data);
        })
        .catch((error) => {
          console.error("Error fetching analytics:", error);
        });
    }
  }, [selectedStep, selectedTrainingId]);


  const handleTrainingSelect = (value: string) => {
    setTrainingId(value);
  };

  const [getUserById] = useLazyQuery(GET_USER_BY_ID, {
    onCompleted(data) {
      if (!!data.userWithRolesById) {
        form.setFieldsValue({
          roleCode: data.userWithRolesById.roles[0].roleName,
        });
        const orgId = data.userWithRolesById.organization;
        getOrganizationById({ variables: { organizationByIdId: orgId } });
      } else {
        alertMessage({ type: "error", message: "User not found" });
        setLoading(false);
      }
    }
  });

  const [getOrganizationById] = useLazyQuery(GET_ORG_BY_ID, {
    onCompleted(data) {
      if (!!data.organizationById) {
        const trainingPackage = data.organizationById.trainingPackage;
        if (trainingPackage) {
          const trainingPackageId = trainingPackage._id;
          getTrainingModuleById({ variables: { trainingPackageByIdId: trainingPackageId } });
        } else {
          setLoading(false);
        }
      } else {
        alertMessage({ type: "error", message: "Organization not found" });
        setLoading(false);
      }
    }
  });

  const [getTrainingModuleById] = useLazyQuery(GET_TRAINING_MODULE__BY_ID, {
    onCompleted(data) {
      const trainingData = data.trainingPackageById;
      if (trainingData && trainingData.trainings && trainingData.trainings.length > 0) {
        // 1. Get analytics training names
        const analyticsTrainingNames = analyticData.map((a: any) => a.name);

        // 2. Sort trainings: those with analytics first
        const sortedTrainings = [...trainingData.trainings].sort((a, b) => {
          const aHasAnalytics = analyticsTrainingNames.includes(a.name);
          const bHasAnalytics = analyticsTrainingNames.includes(b.name);
          if (aHasAnalytics === bHasAnalytics) return 0;
          return aHasAnalytics ? -1 : 1;
        });

        // 3. Map to options
        const options = sortedTrainings.map((training: any) => ({
          value: training._id,
          label: training.name
        }));

        setOptions(options);
      } else {
        alertMessage({ type: "error", message: "Trainings not found" });
        setLoading(false);
      }
    },
    onError(error) {
      alertMessage({ type: "error", message: error.message });
      setLoading(false);
    }
  });

  useEffect(() => {
    if (!!location.state?.userId) {
      getUserById(
        {
          variables: {
            userWithRolesByIdId: location.state?.userId
          }
        });
    }
  }, [location.state?.userId, getUserById]);

  const [getQuestionDetails] = useLazyQuery(GET_QUESTION_DETAILS, {
    onCompleted: (data) => {
      setScenarios(data?.getQuestionDetails || []);
      setLoading(false);
      setIsLoadingData(false); // Set loading to false when data is received
    },
    onError: (error) => {
      alertMessage({ type: "error", message: error.message });
      setLoading(false);
      setIsLoadingData(false); // Set loading to false on error
    }
  });

  useEffect(() => {
    if (moduleName) {
      getQuestionDetails({
        variables: {
          userId: (location.state as UserData)?.userId || '',
          moduleName: moduleName || '',
        },
      });
    }
  }, [moduleName, getQuestionDetails]);

  const createStepHandler = (selectedOption: any) => {
    setLoading(true); // Set main loading state to true
    const selectedTraining = options.find(option => option.value === selectedOption)?.label;
    if (selectedTraining) {
      setModuleName(selectedTraining);
      setSteps([]);
      const selectedModuleData = analyticData.find((data: any) => data.name === selectedTraining);
      if (selectedModuleData) {
        setSteps(selectedModuleData);
      }
      getQuestionDetails({
        variables: {
          userId: (location.state as UserData)?.userId || '',
          moduleName: selectedTraining || '',
        },
      });
    }
  };

  const setAlertMessageFun = (message: string, type: any) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    alertMessage({ type: type, message: message });
    setTimeout(() => {
      alertMessage({
        type: 'success',
        message: ''
      });
    }, resetAlertTimer);
  };

  const createStepHandlers = (selectedTraining: string, analyticData: any) => {
    analyticData.forEach((data: any) => {
      if (data.name === selectedTraining) {
        setSteps(data);
        setModuleName(selectedTraining);
        form.setFieldValue('grade', data?.grade[0]?.grade);
        setSelectedGradeId(data?.grade[0]?._id);
        getQuestionDetails({
          variables: {
            userId: (location.state as UserData)?.userId || '',
            moduleName: selectedTraining || '',
          },
        });
      }
    });
  };

  const setGradeHandlers = (grade: string, analyticData: any) => {
    if (!!selectedGradeId) {
      createGrade({
        variables: {
          gradingInput: {
            uniqueId: steps?.attempts[0]?.uniqueId,
            userId: location.state?.userId,
            grade: grade,
            _id: selectedGradeId
          }
        }
      });
    } else {
      createGrade({
        variables: {
          gradingInput: {
            uniqueId: steps?.attempts[0]?.uniqueId,
            userId: location.state?.userId,
            grade: grade,
          }
        }
      });
    }

  };


  const { loading: analyticsLoading } = useQuery(GET_USER_ANALYTICS, {
    variables: {
      analyticsByUserIdId: location.state?.userId
    },
    onCompleted(data) {
      const analytics = data?.analyticsByUserId;
      if (analytics.length > 0) {
        const _analytics = analytics.map((_singleAnalytics: any, index: number) => {
          return _singleAnalytics;
        });
        setAnalyticData(_analytics);
        let optionsData = _analytics.map((_analytic: any) => {
          return (
            {
              label: _analytic.name,
              value: _analytic.name
            }
          );
        });
        setOptions(optionsData);
        createStepHandlers(optionsData[0].label, _analytics);
      } else {
        setNoData(true); 
        setLoading(false);
      }
      // setGrades(gradesArr);
    },
    onError(error) {
      setAlertMessageFun(error.message, "error");
      setLoading(false); 
    }
  });

  const [createGrade, { loading: createGradeLoading }] = useMutation(CREATE_GRADE, {
    onCompleted() {
      setAlertMessageFun("Grade updated successfully", "success");
    },
    onError(error) {
      setAlertMessageFun(error.message, "error");
    }
  });

  const handleTabClick = (scenarioId: string) => {
    if (selectedScenario === scenarioId) {
      setSelectedScenario("");
    } else {
      setSelectedScenario(scenarioId);
    }
  };


  const showIconOfResult = (result: TrainingStatus | boolean | string) => {
    // Handle string "true"/"false" values
    if (typeof result === 'string') {
      if (result.toLowerCase() === 'true') {
        return (
          <Tooltip title="Correct">
            <Tag color="success"><TaskAltIcon /></Tag>
          </Tooltip>
        );
      }
      if (result.toLowerCase() === 'false') {
        return (
          <Tooltip title="Wrong">
            <Tag color="error"><CancelIcon /></Tag>
          </Tooltip>
        );
      }
    }

    // Handle boolean true/false values
    if (typeof result === 'boolean') {
      return result ? (
        <Tooltip title="Correct">
          <Tag color="success"><TaskAltIcon /></Tag>
        </Tooltip>
      ) : (
        <Tooltip title="Wrong">
          <Tag color="error"><CancelIcon /></Tag>
        </Tooltip>
      );
    }

    // Handle TrainingStatus enum values
    switch (result) {
      case TrainingStatus.COMPLETED:
        return (
          <Tooltip title="Correct">
            <Tag color="success"><TaskAltIcon /></Tag>
          </Tooltip>
        );
      case TrainingStatus.INCOMPLETED:
        return (
          <Tooltip title="Wrong">
            <Tag color="error"><CancelIcon /></Tag>
          </Tooltip>
        );
      case TrainingStatus.SKIPPED:
        return (
          <Tooltip title="Skipped">
            <Tag color="warning"><SkipNextIcon /></Tag>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const setGradeHandler = (grade: string) => {
  };

  return (
    <>
      {loading || analyticsLoading || createGradeLoading ? (
        <Box display={'flex'} justifyContent={'center'} sx={{ mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : noData ? ( // Check if no data is available
        <Box display={'flex'} justifyContent={'center'} sx={{ mt: 5 }}>
          <Typography variant="h6">No Recorded Attempts Yet</Typography>
        </Box>
      ) : options.some(option => option.label === IQTraining.NCLEX_MODULE) && moduleName === IQTraining.NCLEX_MODULE ? (
        <FirstComponent
          form={form}
          options={options}
          loading={loading}
          createGrade={createGrade}
          createGradeLoading={createGradeLoading}
          createStepHandler={createStepHandler}
          setGradeHandler={setGradeHandler}
          handleTabClick={handleTabClick}
          selectedScenario={selectedScenario}
          scenarios={scenarios}
          showIconOfResult={(result) => showIconOfResult(result) || <></>}
          setSelectedScenario={setSelectedScenario}
          alertMessage={alertMessage}
          moduleName={moduleName}
          setModuleName={setModuleName}
        />
      ) : (
        <SecondComponent
          selectedStep={selectedStep}
          form={form}
          steps={steps}
          analyticData={analyticData}
          options={options}
          theme={theme}
          loading={loading}
          createGrade={createGrade}
          createGradeLoading={createGradeLoading}
          formatCompletedAt={formatCompletedAt}
          handleTrainingSelect={handleTrainingSelect}
          createStepHandler={createStepHandler}
          analyticsLoading={analyticsLoading}
          setGradeHandlers={setGradeHandlers}
          stepData={stepData}
          handleStepClick={handleStepClick}
          selectedScenario={selectedScenario}
          scenarios={scenarios}
          showIconOfResult={(result) => showIconOfResult(result) || <></>}
          setSelectedScenario={setSelectedScenario}
          moduleName={moduleName}
          setModuleName={setModuleName}
          alertMessage={alertMessage}
        />
      )}
    </>
  );
};

type FirstComponentProps = {
  form: FormInstance;
  options: any[];
  // grades: any[];
  loading: boolean;
  createGrade: any;
  createGradeLoading: boolean;
  createStepHandler: (selectedOption: any) => void;
  setGradeHandler: (grade: string) => void;
  handleTabClick: (scenarioId: string) => void;
  selectedScenario: string;
  scenarios: Scenario[];
  showIconOfResult: (result: TrainingStatus | boolean | string) => JSX.Element;
  setSelectedScenario: (scenarioId: string) => void;
  alertMessage: (val: Error) => void;
  moduleName: string;
  setModuleName: (moduleName: string) => void;
};

const FirstComponent: React.FC<FirstComponentProps> = ({
  form,
  options,
  //grades,
  createGradeLoading,
  createStepHandler,
  setGradeHandler,
  handleTabClick,
  selectedScenario,
  scenarios,
  showIconOfResult,
  setSelectedScenario,
  alertMessage,
  setModuleName
}) => (
  <>
    <Grid2 container spacing={2} sx={{ mx: 2, justifyContent: "space-between" }}>
      <Grid2 xs={12} md={8}>
        <Form form={form} initialValues={{ "trainingAnalytics": options[0]?.value, }} style={{ display: 'flex', gap: "50%" }}>
          <Box sx={{ flexGrow: 1 }}>
            <FormItemSelect
              value={options}
              name={'trainingAnalytics'}
              label={'Training'}
              showLable={false}
              setValue={(v) => createStepHandler(v)} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            {/* <FormItemSelect
              value={grades}
              name={'grade'}
              label={'Grade'}
              setValue={(v) => setGradeHandler(v)} /> */}
          </Box>
        </Form>
      </Grid2>
    </Grid2>

    <Grid2 container spacing={2}>
      {scenarios.map((scenario, i) => (
        <Paper elevation={2} sx={{ mx: 2, mb: 2 }} key={`paper-${scenario.scenarioId}`} style={{ width: '100%' }}>
          <Grid2 xs={12} key={scenario.scenarioId}>
            <div
              onClick={() => handleTabClick(scenario.scenarioId)}
              style={{ cursor: 'pointer', padding: '10px' }}
            >
              <Typography variant="subtitle1" style={{ display: 'inline-block', width: '200px' }}>{`Scenario Id: ${scenario.scenarioId}`}</Typography>
              <Typography variant="subtitle1" style={{ display: 'inline-block', width: 'calc(100% - 200px)' }}>{`Scenario: ${scenario.scenarioName}`}</Typography>
            </div>
          </Grid2>
          {selectedScenario === scenario.scenarioId && (
            <>
              {scenario.attempts.map((attempt, index) => (
                <Grid2 container padding={2} key={`attempt-${index}`}>
                  <Grid2 xs={6} sm={3}>
                    <Typography variant="subtitle1">{`Timestamp: ${attempt.timestamp}`}</Typography>
                  </Grid2>
                  <Grid2 xs={6} sm={3}>
                    <Typography variant="subtitle1">{`Time Taken: ${attempt.timeTaken}`}</Typography>
                  </Grid2>
                  <Grid2 xs={6} sm={3}>
                    {/* Render your video component here */}
                  </Grid2>
                  <Grid2 xs={6} sm={3}>
                    {showIconOfResult(attempt.passed)}
                  </Grid2>
                </Grid2>
              ))}
            </>
          )}
        </Paper>
      ))}
    </Grid2>
  </>
);

type SecondComponentProps = {
  form: FormInstance;
  options: any[];
  selectedStep: number | null;
  //grades: any[];
  steps: any;
  analyticData: any;
  loading: boolean;
  createGrade: any;
  theme: any;
  analyticsLoading: any;
  createGradeLoading: boolean;
  createStepHandler: (selectedOption: any) => void;
  handleTrainingSelect: (value: string) => void;
  setGradeHandlers: (grade: string, analyticData: any) => void;
  handleStepClick: (stepNo: number) => void;
  stepData: any;
  selectedScenario: string;
  scenarios: Scenario[];
  showIconOfResult: (result: TrainingStatus | boolean | string) => JSX.Element;
  setSelectedScenario: (scenarioId: string) => void;
  moduleName: string;
  setModuleName: (moduleName: string) => void;
  alertMessage: (val: Error) => void;
  formatCompletedAt: (completedAt: string) => string;
};

const SecondComponent: React.FC<SecondComponentProps> = ({
  form,
  options,
  // grades,
  steps,
  theme,
  analyticData,
  analyticsLoading,
  loading,
  createGrade,
  createGradeLoading,
  createStepHandler,
  setGradeHandlers,
  formatCompletedAt,
  handleStepClick,
  handleTrainingSelect,
  stepData,
  selectedStep,
  showIconOfResult,
  setSelectedScenario,
  moduleName,
  setModuleName,
  alertMessage,
}) => {
  // Filter out duplicate steps based on stepNo and stepName
  const uniqueStats = steps?.attempts
  ?.flatMap((attempt: any) => attempt.stats || [])
  ?.reduce((acc: any[], stat: any) => {
    const existingStatIndex = acc.findIndex((s) => s.stepNo === stat.stepNo && s.stepName === stat.stepName);

    if (existingStatIndex === -1) {
      acc.push(stat); // First occurrence, add it
    } else {
      // Compare timestamps and keep only the latest attempt
      const existingStat = acc[existingStatIndex];
      const existingTime = new Date(existingStat.completedAt).getTime();
      const newTime = new Date(stat.completedAt).getTime();

      if (newTime > existingTime) {
        acc[existingStatIndex] = stat; // Replace with the latest attempt
      }
    }

    return acc;
  }, []);

  return (
    <>
      <Grid2 container spacing={2} sx={{ mx: 2, justifyContent: "space-between" }}>
        <Grid2 xs={12} md={8}>
          <Form
            form={form}
            initialValues={{ "trainingAnalytics": options[0]?.value }}
            style={{ display: 'flex', gap: "50%" }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <FormItemSelect
                value={options}
                name={'trainingAnalytics'}
                label={'Training'}
                showLable={false}
                setValue={(v) => createStepHandler(v)}
              />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              {/* <FormItemSelect
                value={grades}
                name={'grade'}
                label={'Grade'}
                setValue={(v) => setGradeHandlers(v, analyticData)} /> */}
            </Box>
          </Form>
        </Grid2>
        <Grid2>
          {steps?.attempts?.length > 0 && (
            <Chip
              label={`Accuracy : ${Number(steps?.attempts[0].accuracy) % 1 === 0
                ? Number(steps?.attempts[0].accuracy)
                : parseFloat(steps?.attempts[0].accuracy).toFixed(2)}%`}
              variant="outlined"
            />
          )}
        </Grid2>
      </Grid2>
      <Divider sx={{ mb: 2 }} />
      {uniqueStats && uniqueStats.length > 0 ? (
        uniqueStats.map((stat: any, i: number) => (
          <Paper elevation={2} sx={{ mx: 2, mb: 2 }} key={`key${i}`}>
            <Grid2 container padding={2}>
              <Grid2 xs={10} sm={8} container onClick={() => handleStepClick(stat?.stepNo)} style={{ cursor: 'pointer' }}>
                <Grid2 xs={2} sm={2}>
                  {`Step ${stat?.stepNo}`}
                </Grid2>
                <Grid2 xs={10} sm={9}>
                  {stat?.stepName}
                </Grid2>
              </Grid2>
            </Grid2>

            {selectedStep === stat?.stepNo && stepData?.analyticsByUserIdTrainingIdStepNo?.length > 0 && (
              <Grid2 container padding={2}>
                {stepData.analyticsByUserIdTrainingIdStepNo.map((group: any, index: number) =>
                  group.attempts.map((attempt: any, attemptIndex: number) =>
                    attempt.stats
                      .filter((s: any) => s.stepNo === selectedStep)
                      .map((s: any, statIndex: number) => (
                        <Grid2 xs={12} key={`stat-${index}-${attemptIndex}-${statIndex}`}>
                          <Grid2 container padding={2}>
                            <Grid2 xs={6} sm={5}>
                              <Typography fontSize="15px" variant="subtitle1">{`Attempt Date: ${formatCompletedAt(s.completedAt)}`}</Typography>
                            </Grid2>
                            <Grid2 xs={6} sm={3}>
                              <Typography fontSize="15px" variant="subtitle1">{`Time Taken: ${s.timeTaken} seconds`}</Typography>
                            </Grid2>
                            <Grid2 xs={6} sm={3}>
                              {showIconOfResult(s.result)}
                            </Grid2>
                          </Grid2>
                        </Grid2>
                      ))
                  ))}
              </Grid2>
            )}
          </Paper>
        ))
      ) : (
        <Box marginX={theme.spacing(60)}>
          <Paper elevation={0}>
            <Typography variant="h6" paddingTop={theme.spacing(2)} textAlign={"center"}>
              No data available
            </Typography>
          </Paper>
        </Box>
      )}
    </>
  );
};

export default CreateAnalytics;